import { useQuery } from '@tanstack/react-query';
import { FC, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { Input } from '@/components/Forms';
import Text from '@/components/Text';
import useNetwork from '@/hooks/dq/useNetwork';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { EditorialQueuePage } from '@/types/resources';
import { api } from '@/utils/api';
import { EditorialFacebookPostNetwork } from '@/utils/enums';
import { scrollBarStyles } from '@/utils/theme';

const Dropdown = styled.div`
  position: relative;
`;

const Arrow = styled.svg<{ $opened: boolean }>`
  color: ${({ theme }) => theme.colors.gray};
  transition: transform 0.15s ease-out;

  ${({ $opened }) =>
    $opened &&
    css`
      transform: rotate(180deg);
    `}
`;

const Label = styled(Text)`
  flex: 1;
  padding: 0 8px;
  text-align: left;
`;

const Button = styled.button.attrs({ type: 'button' })<{ $opened: boolean }>`
  display: flex;
  align-items: center;
  width: 220px;
  max-width: 100%;
  height: 32px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  border: 1px solid
    ${({ theme, $opened }) => theme.colors[$opened ? 'gray' : 'lightGray']};
  padding: 0 12px;
  flex-shrink: 0;

  &:hover {
    border-color: ${({ theme }) => theme.colors.gray};
  }
`;

const Thumbnail = styled.img`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

const Options = styled.div`
  position: absolute;
  box-shadow: 0px 0px 8px 0px #2222221f;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  z-index: 2;
  padding: 4px;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  max-height: 400px;
  overflow-y: auto;
  ${scrollBarStyles};

  ${Button} {
    border: 0;
    height: auto;
    padding-top: 12px;
    padding-bottom: 12px;

    &:hover {
      background-color: ${({ theme }) => theme.colors.grayscaleLight};
    }
  }
`;

const SearchInput = styled(Input)`
  border: 0;
  height: 100%;
  width: 100%;
  padding: 0;
  font-size: 12px;
  outline: none !important;
`;

interface Props {
  activePage: EditorialQueuePage;
}

const PagesSelection: FC<Props> = ({ activePage }) => {
  const [isOpen, setIsOpen] = useState(false);

  const [search, setSearch] = useState('');

  const dropdownRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  const { status } = useParams();

  const [network, networkLabel] = useNetwork();

  const { data } = useQuery(
    ['editorial_distribution_queue_channels'],
    () => api.get<never, EditorialQueuePage[]>('/v1/editorial/queue/channels'),
    {
      staleTime: 120_000,
    },
  );

  const pages = useMemo(() => {
    let key: keyof EditorialQueuePage;

    switch (network) {
      case EditorialFacebookPostNetwork.FACEBOOK:
        key = 'hasFacebook';
        break;
      case EditorialFacebookPostNetwork.INSTAGRAM:
        key = 'instagramAccountId';
        break;
      case EditorialFacebookPostNetwork.THREADS:
        key = 'threadsAccountId';
        break;
      case EditorialFacebookPostNetwork.PINTEREST:
        key = 'pinterestAccountId';
        break;
      case EditorialFacebookPostNetwork.TWITTER:
        key = 'twitterAccountId';
        break;
      case EditorialFacebookPostNetwork.REELS:
        key = 'hasFacebook';
        break;
      case EditorialFacebookPostNetwork.TEXT_STATUS:
        key = 'hasFacebook';
        break;
      case EditorialFacebookPostNetwork.WHATSAPP:
        key = 'whatsAppNewsletterId';
        break;
      case EditorialFacebookPostNetwork.BLUESKY:
        key = 'hasBlueskyAccount';
        break;
      case EditorialFacebookPostNetwork.YOUTUBE_SHORTS:
        key = 'hasYoutube';
        break;
      case EditorialFacebookPostNetwork.LINKEDIN:
        key = 'hasLinkedin';
        break;
      case EditorialFacebookPostNetwork.TIKTOK:
        key = 'hasTiktok';
        break;
      case EditorialFacebookPostNetwork.MSN:
        key = 'hasMsn';
        break;
      case EditorialFacebookPostNetwork.REDDIT:
        key = 'hasReddit';
        break;
    }
    return data?.filter((page) => !!page[key]);
  }, [data, network]);

  const onSelect = (page: EditorialQueuePage) => {
    const path = `/general/editorial-distribution-queue/${page.id}/${status}/${networkLabel}`;

    navigate(path, {
      replace: true,
    });

    setSearch('');
    setIsOpen(false);
  };

  const filteredPages = useMemo(() => {
    if (!search) {
      return pages;
    }

    if (!pages) {
      return null;
    }

    return pages.filter((p) =>
      p.name.toLowerCase().includes(search.toLowerCase()),
    );
  }, [search, pages]);

  useOnClickOutside(dropdownRef, () => {
    setIsOpen(false);
    setSearch('');
  });

  return (
    <Dropdown>
      <Button
        $opened={isOpen}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(true);
        }}
      >
        {!isOpen ? (
          <>
            {!!activePage.thumbnail && (
              <Thumbnail src={activePage.thumbnail} alt={activePage.name} />
            )}
            <Label $type="smallHeadline" as="span">
              {activePage.name}
            </Label>
            <Arrow
              $opened={isOpen}
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M8 10.5L12 5.5H4L8 10.5Z" fill="currentColor" />
            </Arrow>
          </>
        ) : (
          <SearchInput
            placeholder="Search by name"
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        )}
      </Button>
      {isOpen && (
        <Options ref={dropdownRef}>
          {filteredPages?.map((page) => (
            <Button
              onClick={() => onSelect(page)}
              key={page.id}
              $opened={false}
            >
              {!!page.thumbnail && (
                <Thumbnail src={page.thumbnail} alt={page.name} />
              )}
              <Label $type="smallHeadline" as="span">
                {page.name}
              </Label>
            </Button>
          ))}
        </Options>
      )}
    </Dropdown>
  );
};

export default PagesSelection;
